import React, { useMemo, useRef } from 'react';
import { parseFormula, FORMULA_FUNCTIONS } from '../../../../shared/reportFormula';
import { calculationInfo, formulaFields, formulaRefs } from './calculations';

const FORMATS = [
  { value: '', label: 'Automatic' },
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percentage' },
  { value: 'currency', label: 'Currency' },
];

/**
 * Edits one calculation: its name, the value it works on (or its formula),
 * and how it is shown.
 */
const CalculationEditor = ({ calc, form, labelOf, onChange, onRemove, onDone }) => {
  const info = calculationInfo(calc.type);
  const formulaRef = useRef(null);
  const fields = useMemo(() => formulaFields(form, calc, labelOf), [form, calc, labelOf]);
  const parsed = calc.type === 'formula' ? parseFormula(calc.formula) : null;
  const refs = parsed?.ok ? formulaRefs(calc.formula, fields, calc.refs) : {};
  const unknown = parsed?.ok ? parsed.fields.filter((f) => !refs[f]) : [];
  const update = (patch) => {
    const next = { ...calc, ...patch };
    if (next.type === 'formula') next.refs = formulaRefs(next.formula, fields, calc.refs);
    onChange(next);
  };

  const insert = (text) => {
    const el = formulaRef.current;
    const value = calc.formula || '';
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const next = `${value.slice(0, start)}${text}${value.slice(end)}`;
    update({ formula: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    });
  };

  let status = null;
  if (parsed && !parsed.ok && calc.formula) status = { problem: true, text: parsed.error };
  else if (unknown.length) status = { problem: true, text: `${unknown.map((u) => `[${u}]`).join(', ')} ${unknown.length === 1 ? 'is' : 'are'} not in this report. Use the buttons below to add a value by name.` };
  else if (parsed?.ok) status = { problem: false, text: 'The formula is worked out on each row and on the total.' };

  return (
    <div className="rf-calc-editor" role="group" aria-label={`Calculation: ${calc.label}`}>
      <div className="rf-calc-editor-head">
        <strong>{info?.name}</strong>
        <span className="rf-muted">{info?.hint}</span>
      </div>
      <label className="rf-format-field">
        <span>Name</span>
        <input aria-label="Calculation name" value={calc.label} maxLength={120} onChange={(e) => onChange({ ...calc, label: e.target.value })} />
      </label>

      {calc.type === 'formula' ? (
        <>
          <label className="rf-format-field">
            <span>Formula</span>
            <textarea ref={formulaRef} aria-label="Formula" rows={3} spellCheck={false} value={calc.formula || ''} placeholder="[Gross profit] / [Turnover]"
              aria-invalid={status?.problem || undefined} onChange={(e) => update({ formula: e.target.value })} className="rf-formula-input" />
          </label>
          {status && <p className={`rf-field-note ${status.problem ? 'rf-field-note--problem' : ''}`} role={status.problem ? 'alert' : undefined}>{status.text}</p>}
          <div className="rf-formula-insert" aria-label="Add to the formula">
            {fields.map((f) => (
              <button key={f.key} type="button" className="rf-insert" onClick={() => insert(`[${f.name}]`)} title={`Add [${f.name}]`}>{f.name}</button>
            ))}
            {['+', '-', '*', '/', '(', ')'].map((op) => (
              <button key={op} type="button" className="rf-insert rf-insert--op" onClick={() => insert(` ${op} `.replace('( ', '(').replace(' )', ')'))} aria-label={`Add ${op}`}>{op}</button>
            ))}
          </div>
          <p className="rf-field-note">Use + - * / with brackets and numbers, and {FORMULA_FUNCTIONS.map((f) => `${f}()`).join(', ')}. Dividing by zero leaves the cell empty.</p>
        </>
      ) : (
        <label className="rf-format-field">
          <span>Of</span>
          <select aria-label="Value to calculate" value={calc.measure || ''} onChange={(e) => onChange({ ...calc, measure: e.target.value })}>
            {form.measures.map((m) => <option key={m} value={m}>{labelOf(m)}</option>)}
          </select>
        </label>
      )}

      <label className="rf-format-field">
        <span>Show as</span>
        <select aria-label="Show the calculation as" value={calc.format || ''} onChange={(e) => onChange({ ...calc, format: e.target.value || null })}>
          {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </label>

      <div className="rf-calc-editor-actions">
        <button type="button" className="rf-btn rf-btn--ghost rf-btn--small" onClick={onRemove}>Remove</button>
        <button type="button" className="rf-btn rf-btn--primary rf-btn--small" onClick={onDone}>Done</button>
      </div>
    </div>
  );
};

export default CalculationEditor;
